/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */ 
import React, { forwardRef, useCallback, useEffect, useImperativeHandle }  from 'react'; // we need this to make JSX
import { useForm } from 'react-hook-form';
import { Grid, Typography, Divider, IconButton, Tooltip } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import { AUTH_STRINGS } from '../../../../constants/strings';
import { Logger } from '../../../../libs/utils/logger';
import { FxTextEdit } from '../../../Input/FxText/FxTextEdit';
import { FxButton } from '../../../Action/FxButton';
import usa_state from '../../../Utils/usa_states.json';
import FxLabelView from '../../../Input/FxLabel/FxLabelView';
import FxCardHeader from '../../../Container/FxCardHeader';
import FxCard from '../../../Container/FxCard';
import FxCardBody from '../../../Container/FxCardBody';
import FxCardFooter from '../../../Container/FxCardFooter';
import { getKey, setKey } from '../../../../libs/utils/storageManager';
import {
  handleAddressChange,
  handleKeyDownChange,
  handleZipChange,
  setPhoneValidationOptions,
  setZipValidationOptions,
  updateComponentParams,
  setAddressLine1Validation,
  setItinValidationOptions,
  setSsnValidationOptions,
  handleSecIdChange,
  handleFieldChange,
  handleDateValidation,
  validateNoPastDate,
  fetchDataFromState,
  ShowInfoToolTip,
  isNullOrUndefined,
  ageLimit
} from '../../../Utils/CommonBaseClass';
import { ReactComponent as ContactsIcon } from '../../../../assets/svg/contacts-emty.svg';
import { ReactComponent as PhoneVerificationICon } from '../../../../assets/svg/verification.svg';
import { FxDateEdit } from '../../../Input/FxDate/FxDateEdit';
import FxPhoneEdit from '../../../Input/FxPhone/FxPhoneEdit';
import { FxTextEditSSN } from '../../../Input/FxSSN/FxTextEditSSN';
import { RegisterComponent } from '../../../../libs/saga/dataSaga';
import { useDispatch, useSelector } from 'react-redux';
import FxMaterialSelect from '../../../Input/FxSelect/FxMaterialSelect';
import {secondaryIdentificationIdValidation} from '../../../Utils/CommonBaseClass'
import issuance_countries from '../../../../libs/utils/country/country.json'
import { ReactComponent as InfoIcon } from '../../../../assets/svg/signup/info-tooltip.svg';
import {FieldErrorMessageRender} from '../Common/FieldErrorMessageRender';
import moment from 'moment';
import HttpClient from '../../../../libs/utils/httpClient';
import { getCustomerUrl } from '../../../../libs/utils/utils';
import { saveNodeData } from '../../../../libs/utils/autoSaveField';
const httpClient = HttpClient.getClient();

/**
 * Component: SignUpIndividualDetails
 * Usage: Customer SignUp Individual Details Add page
 */

Logger.debug("SignUpIndividualDetails.tsx", "Individual SignUp initializing")

// Phone validation constant
const setEmailValidationOptions = {
    required: true,
    maxLength: 250,
    pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,40}$/i,
        message: AUTH_STRINGS.ERRORS.EMAIL_INVALID_REGEXP,
    },
};

// Define the type for the exposed functions
export interface SignUpIndividualDetailsRef {
    submitForm: () => Promise<{
      success: boolean;
      errors?: any;
    }>;
  }

export const SignUpIndividualDetails = React.memo(forwardRef<SignUpIndividualDetailsRef, any>((props, ref) => {
    const { register, formState: { errors }, handleSubmit, setValue, control, clearErrors, resetField, setError, watch, getValues, reset } = useForm({mode: 'onChange',reValidateMode:'onChange'});
        ({ props } = RegisterComponent(props));
        const isSmallScreen = useMediaQuery({ query: '(max-width: 600px)' })
        const defaultTaxIdentifier = ((props?.data?.taxIdentifier && props?.data?.taxIdentifier === 'itin') || (!props?.data?.taxIdentifier && props?.data?.itin)) ? 'itin' : 'ssn';
        const taxIdentifier = watch('taxIdentifier') || defaultTaxIdentifier;
        const taxIdentifierData = [
            { label: "SSN", value: "ssn" },
            { label: "ITIN", value: "itin" },
        ];
        const invitationType = getKey('invitationType')
        const allowNontaxIdentifierOnboarding = (getKey('allowNontaxIdentifierOnboarding'))?.toLowerCase();
        
        if(allowNontaxIdentifierOnboarding === 'true'){
            taxIdentifierData.push({ label: "I don't have SSN/ITIN", value: "none" });
        }

        /**
         * This useEffect hook is used to set the initial value of the taxIdentifier field in the form.
         */
        useEffect(() => {
            if((props?.data?.ssn === '' && props?.data?.itin === '') && allowNontaxIdentifierOnboarding === 'true' && !isNullOrUndefined(props?.data?.secondaryIdentificationType)){
                setValue('taxIdentifier', 'none');
            }
        }, [props?.data]);


        /**
         * This useEffect hook is used to fetch the previously saved data from the GET autoSaved API
         */
        useEffect(() => {
            fetchOnboardingFormData();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },[])

 

        /**
         * This method fetches the previously saved data from the GET autoSaved API
         * and sets the values in the form fields
         */
        const fetchOnboardingFormData = async () => {
            const previouslySavedData = await getPreviouslySavedDataFromOnboardingApi();
            if (previouslySavedData?.data?.flatFields) {
                const data = previouslySavedData.data.flatFields;
                updateComponentParams(dispatch, props.id, { autoSavedData: data });

                //Only populate the AutoSaved values if there is any autosaved data available
                if(Object.keys(data).length > 0)
                    updateFormData(data);
            }
        }
     /**
      *  Updates the form values using provided user data.
      *
      * This function maps specific nested fields from the `data` object (structured by individual fields)
      * to flat form field names used in the form. It also handles a conditional case: if both SSN and ITIN
      * are missing but a secondary identification type is present, it sets `taxIdentifier` to `'none'`.
      *
      * Dependencies: Relies on `setValue` from `react-hook-form` to populate form fields.
      */
        const updateFormData = useCallback((data: any) => {
            if (!data) return;

            const safeSetValue = (field: string, value: any) => {
                if (value !== undefined && value !== null) {
                    setValue(field,value);
                }
            };

            safeSetValue('middleName', data['individual.middleName']);
            safeSetValue('itin', data['individual.taxIdentification?.id']);
            safeSetValue('dob', data['individual.dob']);

            if (
                !data['individual.ssn'] &&
                !data['individual.taxIdentification?.id'] &&
                data['individual.secondaryIdentification?.type']
            ) {
                setValue('taxIdentifier', 'none');
            }

            safeSetValue('mobilePhone', data['individual.mobilePhone']);
            safeSetValue('secondaryIdentificationId', data['individual.secondaryIdentification?.id']);
            safeSetValue('secondaryIdentificationType', data['individual.secondaryIdentification?.type']);
            safeSetValue('secondaryIdentificationIssuance', data['individual.secondaryIdentification?.countryOfIssuance']);
            safeSetValue('secondaryIdentificationIssuanceState', data['individual.secondaryIdentification?.stateOfIssuance']);

            safeSetValue('addressLine1', data['individual.mailingAddress[0].addressLine1']);
            safeSetValue('addressLine2', data['individual.mailingAddress[0].addressLine2']);
            safeSetValue('city', data['individual.mailingAddress[0].city']);
            safeSetValue('state', data['individual.mailingAddress[0].state']);
            safeSetValue('zip', data['individual.mailingAddress[0].zip']);
        }, [setValue]);


        /**
         * Calling the GET autosave api to get the previously saved data
         * @returns 
         */
        const getPreviouslySavedDataFromOnboardingApi = async () => {
            try {
                let apiName: any = '/onboardingFormData'
                return await httpClient.get(getCustomerUrl(apiName,false));
            } catch (err) {
                Logger.error("SignUpIndividualDetails.tsx", "getPreviouslySavedDataFromOnboardingApi", "error", err);
                return err;
            }
        }

        /**
        * Exposes the `submitForm` method to parent components via `ref`.
        * 
        * - Uses the `handleSubmit` function (likely from `react-hook-form`) to validate and submit the form.
        * - On successful form submission, it sets the `result` object to `{ success: true, errors: {} }`.
        * - If there are form validation errors, it sets the `result` object to `{ success: false, errors: <form errors> }`.
        * - Returns the `result` object to indicate whether the form submission was successful or not, including any validation errors.
        */
        useImperativeHandle(ref, () => ({
            submitForm: async (options: { isClearError?: boolean } = { isClearError: false }) => {
                let result:any = {};
                await handleSubmit(
                    async (data) => {
                        fetchOnboardingFormData();
                        await onSubmit(data);
                        result = { success: true, data, errors:{}, currentPage: 1 };
                    },
                    (formErrors) => {
                        if(options?.isClearError){
                            clearErrors();
                        }
                        result = { success: false, errors: formErrors, currentPage: 1 };
                    }
                )();
                return result;
            }
        }));

        const dispatch = useDispatch();

        useEffect(() => {
            updateComponentParams(dispatch, props.id, {isLoading: false, ssn: false, itin: false})
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },[])

        useEffect(() => {
            // Reset the value of the selected taxIdentifier (SSN or ITIN) field when the taxIdentifier changes
            setValue(taxIdentifier, props?.data?.itin || props?.data?.ssn);
            
            // Clear any errors for the field that is not selected
            clearErrors(taxIdentifier === 'ssn' ? 'itin' : 'ssn');
        }, [taxIdentifier]);
      
        /**
         * Method to handle on submit event
         * @param data :data
         */
        async function onSubmit(data: any) {
            if (watch('taxIdentifier') === 'ssn') {
                if (isNullOrUndefined(data?.ssn)) {
                    data['ssn'] = props?.data?.ssn;
                } else if (isNullOrUndefined(data?.itin)) {
                    data['itin'] = props?.data?.itin;
                }
            }
            if (props.onSubmit) {
                setKey('signupIndividualDetailForm','valid');
                updateComponentParams(dispatch, props.id, { isLoading: true })
                props.onSubmit(data,()=>{
                    updateComponentParams(dispatch, props.id, { isLoading: false })
                });
            }
        }


        if (getKey('email') && getKey('phone') && getKey('firstName') && getKey('lastName')) {
            setValue('email', getKey('email'))
            setValue('firstName', getKey('firstName'))
            setValue('lastName', getKey('lastName'))
        }

         /**
         * Method fetches data from the state
         */
         const individualState = useSelector((state: any) =>
            fetchDataFromState(state, 'customer-kyc-individual-data')
        );

        /**
         *  Object set for the Secondary ID Type dropdown
         */
        const secondaryIDTypeData: any = [
            { value: 'DRIVER_LICENSE', label: 'Driver License' },
            { value: 'PASSPORT', label: 'Passport' },
        ];

        /**
         * This method handles the onChange event of the secondaryIdentificationType and secondaryIdentificationIssuanceState dropdowns
         * @param event 
         */
        const handleChange = useCallback((event: any) => {
            if (event.target.name === 'secondaryIdentificationType') {
                saveNodeData('individual-customer-info-page', 'secondaryIdentificationType', event, setValue, getValues,  dispatch, individualState)
            }
            if (event.target.name === 'secondaryIdentificationIssuanceState') {
                saveNodeData('individual-customer-info-page', 'secondaryIdentificationIssuanceState', event, setValue, getValues,  dispatch, individualState)
            }
            if (event.target.name === 'secondaryIdentificationIssuance') {
                saveNodeData('individual-customer-info-page', 'secondaryIdentificationIssuance', event, setValue, getValues,  dispatch, individualState)
            }
        },[]);
        /**
         * Error messages for Date of Birth (DOB) validation:
         * - futureDate: Error if the DOB is a future date (not allowed)
         * - maxDate: Error if the DOB indicates the user is less than 18 years old
         * - minDate: (optional) Error if the DOB is before a very old date (not needed here)
         */ 
        const dobErrorMsg={
            futureDate:AUTH_STRINGS.ERRORS.INDIVIDUAL_FUTURE,
            maxDate:AUTH_STRINGS.ERRORS.INDIVIDUAL_LESS_THAN_18,
            minDate:""
           }

        /**
         * Method handles the  onChange event of the date of birth field
         * @param value 
         * @param fieldName 
         */
        const handleDOBOnChangeEvent = (value: any, fieldName: string) => {
            handleDateValidation("dob", value, setValue, setError, clearErrors, true, ageLimit(18), null, dobErrorMsg);
            isNullOrUndefined(errors?.dob) && saveNodeData('individual-customer-info-page', fieldName, value, setValue, getValues, dispatch, individualState);
        }

        /**
         * Method handles the onchange event of the state field
         * @param e 
         * @param fieldName 
         */
        const handleFieldOnChangeEvents = (e: any, fieldName: string) => {
            saveNodeData('individual-customer-info-page', 'state', e, setValue, getValues,  dispatch, individualState);
        }

        /**
         * Method handles the onChange event of the zip field
         * @param e 
         * @param fieldName 
         */
        const zipFieldOnChangeEventHandler = (e: any, fieldName: string) => {
            handleZipChange(e, setValue, setError, clearErrors);
            saveNodeData('individual-customer-info-page', fieldName, e, setValue, getValues,  dispatch, individualState)
        }

        /**
         * Method handles the onChange event of the secondaryIdentificationType field
         * @param e 
         * @param fieldName 
         */
        const handleSecondaryIdOnChangeEvent = (e: any, fieldName: string) => {
            handleSecIdChange(e, setValue, setError, clearErrors, watch('secondaryIdentificationType'));
            saveNodeData('individual-customer-info-page', fieldName, e, setValue, getValues,  dispatch, individualState);
        }

        /**
         * method Hanldes the onChange event of the expiry date field
         * @param value 
         */
        const handleExpiryDateOnChangeEvent = (value: any) => {
            handleDateValidation("expiryDate", value, setValue, setError, clearErrors, false, null, moment() , { minDate:AUTH_STRINGS.ERRORS.SECONDARY_IDENTIFICATION_PAST });
            saveNodeData('individual-customer-info-page', 'expiryDate', value, setValue, getValues,  dispatch, individualState)
        }

        /**
         * Defines which fields to clear for each selected tax identifier type.
         * Using a Record for string keys and string arrays as values.
         */
        const FIELDS_TO_CLEAR_MAP: Record < string, string[] > = {
            'none': ['ssn', 'itin', 'secondaryIdentificationId'],
            'itin': ['ssn', 'secondaryIdentificationId'],
            'ssn': ['itin', 'secondaryIdentificationId'],
        };


        /**
         * This method handles the onChangeEvent of the Tax Identifier
         * dropdown which decides how to handle the correlateed form values 
         * on the basis of the selected values
         * @param e 
         * @param fieldName 
         */
        const handleTaxIdentifierOnChangeEvent = (e: any, fieldName: string) =>{
            const selectedType = e?.target.value;
            saveNodeData('individual-customer-info-page', fieldName, e, setValue, getValues,  dispatch, individualState);
            const fieldsToClear = FIELDS_TO_CLEAR_MAP[selectedType] || [];
            // Iterate and clear each field
            fieldsToClear.forEach(field => {
                setValue(field, '');
            });
        }
        /**
         * Handles auto-saving of a single form field.
         *
         * This function delegates the save logic to `saveNodeData`, passing in the current field name and value,
         * along with necessary form and state management functions like `setValue`, `getValues`, `dispatch`,
         * and the `individualState`. This ensures that field changes are persisted automatically.
         *
         * @param fieldName - The name of the field being updated.
         * @param value - The new value to be saved.
        */
        const handleAutoSave = (fieldName: string, value: any) => {
            saveNodeData('individual-customer-info-page', fieldName, { target: { value } }, setValue, getValues, dispatch, individualState);
        }

    /**
     * Retrieves the last 4 digits of a masked SSN or ITIN for display purposes.
     *
     * Priority order:
     * 1. If `autoSavedData['individual.ssn']` exists, return its last 4 digits.
     * 2. Else if `data.ssn` exists, return it as-is.
     * 3. Else if `autoSavedData['individual.itin']` exists, return its last 4 digits.
     * 4. Else return `data.itin` as-is.
     *
     * @param {any} data - Object containing `autoSavedData` and `data` with possible SSN/ITIN values.
     * @returns {string | undefined} Masked SSN/ITIN or full value from fallback fields.
     */
    const getMaskedValue = (data: any): string | undefined => {
        try {
            if (data?.autoSavedData?.['individual.ssn']) {
                return data.autoSavedData['individual.ssn'].slice(-4);
            }
            if (data?.data?.ssn) {
                return data.data.ssn;
            }
            if (data?.autoSavedData?.['individual.itin']) {
                return data.autoSavedData['individual.itin'].slice(-4);
            }
            return data?.data?.itin;
        } catch (error) {
            Logger.error("SignupIndividualDetails.tsx", "getMaskedValue", error);
            return undefined;
        }
    };
        return (
            <Grid xs={12} className='signup-detail-page-bg'>
                <Grid container id="create-external-account-main-grid" xs={12}  justifyContent="center">
                    <Grid item xs={12} sm={isSmallScreen?undefined:11} md={isSmallScreen?undefined:7}>
                        <FxCard id="customer-signup-individual-info-card" className={!isSmallScreen?"fx-theme-passport-no-before fx-no-shadow fx-signup-formkyc fx-signup-formkyc-individual-margin fx-individual-details":"fx-theme-passport-no-before fx-no-shadow fx-signup-formkyc fx-individual-details"}>
                            <div className="fx-form-edit-profile" >
                                {props?.autoSavedData && <form id='signup-individual-details-form' onSubmit={handleSubmit(onSubmit)} >
                                    <FxCardHeader id="individual-customer-signup-form-card-header">
                                    <Grid container direction="row" xs={12}  >
                                            <Grid item xs={12} className='fx-signup-formkyc-topblock'>
                                                <Grid item xs={12} container flexWrap={'nowrap'}>
                                                    <Grid item className={"fx-signup-formkyc-topblock-icon"}>
                                                    <ContactsIcon />
                                                    </Grid>
                                                    <Grid item>
                                                    <Typography id="individual-customer-signup-form-card-title" className='signup-detail-page-header' >
                                                        Tell us about yourself
                                                    </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                        <Typography id="individual-customer-signup-form-card-title"  className={isSmallScreen?"fx-word-wrap":'signup-detail-page-sub-title'}>
                                                        To keep your account secure, we need to verify some details. This helps us ensure a safe and compliant experience for you.
                                                        </Typography>
                                                        </Grid>
                                                </Grid>
                                        </Grid>
                                    </FxCardHeader>
                                    <FxCardBody id="customer-signup-individual-info-card#card-body" className="fx-info-card fx-margin-top-reducer" >
                                        <Grid xs={12} sm={12}>
                                            <Grid xs={12} container item className="fx-info-card-form" spacing={2}>
                                                <Grid item xs={12} sm={12}>
                                                    <Divider />
                                                </Grid>
                                                <Grid item xs={12} sm={12}>
                                                    <FxLabelView id="customer-signup-individual-info-label" className="signup-detail-page-sec-sub-title" >MORE ABOUT YOU</FxLabelView>
                                                </Grid>
                                                <Grid container item xs={12} sm={8}>
                                                    <FxTextEdit register={{ ...register("firstName") }} type="text" className={errors.firstName ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: true }} id="signup-firstName" label="First Name" name="firstName" defaultValue={getKey('firstName')} isEditable={false} />
                                                    <FieldErrorMessageRender error={errors} fieldKey='firstName'/>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <FxTextEdit register={{ ...register("middleName") }} type="text" className={errors.middleName ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: false }} id="signup-middleName" label="Middle Name (optional)" name="middleName" defaultValue={props?.data.middleName ? props.data.middleName : ''} 
                                                    onBlur={(e) => handleAutoSave('middleName', e?.target?.value)}/>
                                                </Grid>
                                                <Grid item xs={12} sm={12}>
                                                    <FxTextEdit register={{ ...register("lastName") }} type="text" className={errors.lastName ? "border-error-input fx-input-edit" : "fx-input-edit"} control={control} rules={{ required: true }} id="signup-lastName" label="Last Name " name="lastName" defaultValue={getKey('lastName')} isEditable={false} />
                                                    <FieldErrorMessageRender error={errors} fieldKey='lastName'/>
                                                </Grid>
                                                <Grid item xs={12} sm={12}>
                                                    <FxDateEdit register={{ ...register("dob") }} resetField={resetField} className={errors.dob ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} control={control} rules={{ required: true }} id="signup-dob-textbox" label="Date of Birth" name="dob" variant="outlined" value={props?.autoSavedData?.['individual.dob'] ? props?.autoSavedData?.['individual.dob'] : props?.data.dob ? props.data.dob : ''} setValue={setValue} disableFuture={true} onChange={(value: any) => handleDOBOnChangeEvent(value, 'dob')} ageLimitMessage={dobErrorMsg.maxDate} futureDateMessage={dobErrorMsg.futureDate} defaultCalendarMonth={ageLimit(18)} maxDate={ageLimit(18)} />
                                                    <FieldErrorMessageRender error={errors} fieldKey='dob' type='date' />
                                                </Grid>

                                                {/*** Identification Info Fields ***/}
                                                <Grid container item xs={12} sm={12} display={'flex'} alignItems={'center'}>
                                                    <Grid item >
                                                        <FxLabelView id="business-owner-details-label" className="" >IDENTIFICATION INFO</FxLabelView>
                                                    </Grid>
                                                    <Grid item  xs={1} container justifyContent="center" alignItems="center">
                                                        {ShowInfoToolTip('fx-business-owner-title-tooltip', 'To keep your account secure, we need to verify some details. This helps us ensure a safe and compliant experience for you.', 'bottom-start')}
                                                    </Grid>
                                                </Grid>
                                                
                                                <Grid item xs ={12} sm={12}>
                                                    <FxMaterialSelect
                                                        register={{ ...register("taxIdentifier") }}
                                                        control={control}
                                                        rules={{ required: false }}
                                                        id="signUp-indivudal-form-card-tax-identifier"
                                                        name="taxIdentifier"
                                                        data={taxIdentifierData}
                                                        readOnly={Boolean(props?.data?.ssn !== '' || props?.data?.itin !== '' || ((isNullOrUndefined(props?.data?.ssn) && isNullOrUndefined(props?.data?.itin)) && !isNullOrUndefined(props?.data?.secondaryIdentificationType)))}
                                                        value={defaultTaxIdentifier}
                                                        label="Identification Method"
                                                        setValue={setValue}
                                                        onChange={(e) => handleTaxIdentifierOnChangeEvent(e, 'taxIdentifier')}
                                                    />
                                                </Grid>
                                                
                                                {/** Tax Identifier nodes */}
                                                {taxIdentifier !== 'none' && <>
                                                    <Grid container item xs={12} sm={12} alignItems="center">
                                                        <Grid item xs={isSmallScreen ? 11 : 12} sm={11}>
                                                            <FxTextEditSSN
                                                                key={taxIdentifier}
                                                                register={{ ...register(taxIdentifier) }}
                                                                className={errors.ssn || errors.itin ? "border-error-input fx-input-edit" : "fx-input-edit"}
                                                                error={errors?.ssn || errors?.itin}
                                                                control={control}
                                                                rules={taxIdentifier === 'ssn' ? {...setSsnValidationOptions(true)} : {...setItinValidationOptions(true)}}
                                                                id="signUp-indivudal-form-card-ssn-itin"
                                                                label={`${taxIdentifier === 'ssn'
                                                                        ? `Social Security Number`
                                                                        : `Individual Taxpayer Identification Number`
                                                                    }`}
                                                                placeholder="XXX-XX-XXXX"
                                                                name={taxIdentifier}
                                                                variant="outlined"
                                                                setValue={setValue}
                                                                setError={setError}
                                                                clearErrors={clearErrors}
                                                                defaultValue={getMaskedValue(props)}
                                                                onBlur={(e: any) =>
                                                                    saveNodeData('individual-customer-info-page', taxIdentifier, e, setValue, getValues, dispatch, individualState)
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item xs={isSmallScreen ? 1 : 12} sm={1} container justifyContent="center" alignItems="center">
                                                            {ShowInfoToolTip('fx-business-owner-title-tooltip', 'We will use tax identifier to verify your identity and will not check your credit score', 'bottom-end')}
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={isSmallScreen ? 11 : 12} sm={11} className="fx-individual-details-error-row" >
                                                        <FieldErrorMessageRender error={errors} fieldKey={taxIdentifier === 'ssn' ? 'ssn' : 'itin'} />
                                                    </Grid>
                                                </>}
                                                

                                                {/** Secondary identification nodes */}
                                                {taxIdentifier === 'none' && <>
                                                    <Grid item xs={12} sm={6}>
                                                        <FxMaterialSelect register={{ ...register('secondaryIdentificationType') }} control={control} className={errors['secondaryIdentificationType'] ? "border-error-input fx-input-edit" : "fx-input-edit"} rules={{ required:false }} id={'secondaryIdentificationType'} defaultValue={props?.data?.secondaryIdentificationType ? props?.data?.secondaryIdentificationType : ''} name={'secondaryIdentificationType'} data={secondaryIDTypeData}
                                                            onChange={handleChange} label="Secondary ID Type" setValue={setValue} value={props?.data?.secondaryIdentificationType ? props?.data?.secondaryIdentificationType : ''} />
                                                        <FieldErrorMessageRender error={errors} fieldKey='secondaryIdentificationType'/>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <FxTextEdit register={{ ...register("secondaryIdentificationId") }} className={errors.secondaryIdentificationId ? "border-error-input fx-input-edit login-email fs-mask" : "fx-input-edit login-email fs-mask"} setValue={setValue} control={control} rules={secondaryIdentificationIdValidation(watch('secondaryIdentificationType'),'individual_flow')} id="signup-secondaryIdentificationId" label="ID" name="secondaryIdentificationId" defaultValue={props?.data?.secondaryIdentificationId ? props?.data?.secondaryIdentificationId : ''} 
                                                        onBlur={(e: any) => handleSecondaryIdOnChangeEvent(e, 'secondaryIdentificationId')} />
                                                        <FieldErrorMessageRender error={errors} fieldKey='secondaryIdentificationId'/>
                                                    </Grid>
                                                    {watch("secondaryIdentificationType") === 'DRIVER_LICENSE' &&
                                                        <Grid item xs={12} sm={6}>
                                                            <FxMaterialSelect register={{ ...register('secondaryIdentificationIssuanceState') }} control={control} className={errors['secondaryIdentificationIssuanceState'] ? "border-error-input fx-input-edit" : "fx-input-edit"} rules={{ required: false }} id={'secondaryIdentificationIssuanceState'} name={'secondaryIdentificationIssuanceState'} data={usa_state}
                                                                onChange={handleChange} label="State of Issuance " setValue={setValue} value={props?.data?.secondaryIdentificationIssuanceState ? props?.data?.secondaryIdentificationIssuanceState : ''} />
                                                            <FieldErrorMessageRender error={errors} fieldKey='secondaryIdentificationIssuanceState'/>
                                                        </Grid>}
                                                    {watch("secondaryIdentificationType") !== 'DRIVER_LICENSE' && <Grid item xs={12} sm={6}>
                                                        <FxMaterialSelect register={{ ...register('secondaryIdentificationIssuance') }} control={control} className={errors['secondaryIdentificationIssuance'] ? "border-error-input fx-input-edit" : "fx-input-edit"} rules={{ required: false }} id={'secondaryIdentificationIssuance'} name={'secondaryIdentificationIssuance'} data={issuance_countries}
                                                            onChange={handleChange} value={props?.data?.secondaryIdentificationIssuance ? props?.data?.secondaryIdentificationIssuance : ''} readOnly={false} label="Country of Issuance " setValue={setValue} />
                                                            <FieldErrorMessageRender error={errors} fieldKey='secondaryIdentificationIssuance'/>
                                                    </Grid>}
                                                    <Grid item xs={12} sm={6}>
                                                        <FxDateEdit
                                                            register={{ ...register("expiryDate") }}
                                                            className={
                                                                errors.expiryDate
                                                                    ? "border-error-input fx-input-edit"
                                                                    : "fx-input-edit"
                                                            }
                                                            control={control}
                                                            id="create-collect-onetime-card-form-card-expiry-date-textbox"
                                                            label={"Expiry Date"}
                                                            name="expiryDate"
                                                            type="date"
                                                            variant="outlined"
                                                            value={props?.autoSavedData?.['individual.secondaryIdentification.expiryDate'] ? props?.autoSavedData?.['individual.secondaryIdentification.expiryDate'] : props?.data?.expiryDate ? props?.data?.expiryDate : ''}
                                                            setValue={setValue}
                                                            disablePast={true}
                                                            onChange={(value) => handleExpiryDateOnChangeEvent(value)} 
                                                            rules={{ required: true,validate: validateNoPastDate(AUTH_STRINGS.ERRORS.SECONDARY_IDENTIFICATION_PAST) }}
                                                        />
                                                        <FieldErrorMessageRender error={errors} fieldKey='expiryDate' type='date'/>
                                                    </Grid>
                                                </>}

                                                <Grid item xs={12} sm={12}>
                                                    <FxLabelView id="customer-signup-individual-info-label-primary-address">YOUR CONTACT INFO</FxLabelView>
                                                </Grid>
                                                <FxTextEdit register={{ ...register("address_id") }} className={"fx-hidden"} control={control} rules={{ required: false }} id="signUp-individual-address-id"  name="address_id" variant="outlined" defaultValue={props.data?.address_id} />
                                                <Grid item xs={12} sm={6}>
                                                    <FxTextEdit register={{ ...register('addressLine1') }} control={control} className={errors['addressLine1'] ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} rules={setAddressLine1Validation(true)} id={'addressLine1'} label="Address Line 1 " name={'addressLine1'} variant="outlined" defaultValue={props?.data?.addressLine1 ? props?.data?.addressLine1 : ''} onChange={(e: any) => { handleAddressChange(e, setValue, setError, clearErrors) }} 
                                                    onBlur={(e) => handleAutoSave('addressLine1', e?.target?.value)}/>
                                                    <FieldErrorMessageRender error={errors} fieldKey='addressLine1'/>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FxTextEdit register={{ ...register('addressLine2') }} control={control} className="fx-input-edit fs-mask" id={'addressLine2'} label="Address Line 2 (optional)" name={'addressLine2'} variant="outlined" defaultValue={props?.data?.addressLine2 ? props?.data?.addressLine2 : ''} 
                                                                                                    onBlur={(e) => handleAutoSave('addressLine2', e?.target?.value)}/>
                                                </Grid>
                                                <Grid item xs={12} sm={12}>
                                                    <FxTextEdit register={{ ...register('city') }} control={control} rules={{ required: true }} className={errors['city'] ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} id={'city'} label="City " name={'city'} variant="outlined" defaultValue={props?.data?.city ? props?.data?.city : ''} 
                                                    onBlur={(e) => handleAutoSave('city', e?.target?.value)}/>
                                                    <FieldErrorMessageRender error={errors} fieldKey='city'/>
                                                </Grid>
                                                <Grid item xs={12} sm={!isSmallScreen ? 8 : 12}>
                                                    <FxMaterialSelect register={{ ...register('state') }} control={control} className={errors['state'] ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask" } rules={{ required: true }} id={'state'} name={'state'} data={usa_state}
                                                    label="State " setValue={setValue} value={props?.data['state'] ? props?.data['state'] : ''} onChange={(e) => handleAutoSave('state', e?.target?.value)}/>
                                                    <FieldErrorMessageRender error={errors} fieldKey='state'/>
                                                </Grid>
                                                <Grid item xs={12} sm={!isSmallScreen ? 4 : 12}>
                                                    <FxTextEdit register={{ ...register('zip') }} control={control} rules={setZipValidationOptions(true)} className={errors['zip'] ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} id={'zip'} label="Zip" name={'zip'} variant="outlined" defaultValue={props?.data?.zip ? props?.data?.zip : ''} onChange={(e: any) => zipFieldOnChangeEventHandler(e, 'zip')} onKeyDown={(e: any) => { handleKeyDownChange(e, setValue) }} />
                                                    <FieldErrorMessageRender error={errors} fieldKey='zip'/>
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={6}>
                                                <FxPhoneEdit register={register} control={control} rules={setPhoneValidationOptions(true)} className={errors.mobilePhone ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit fs-mask"} id="signup-mobilePhone" name="mobilePhone" label="Phone Number" defaultCountryCode={getKey('countryCode')} defaultValue={props?.data?.mobilePhone ?? ''} isEditable={invitationType !== "SMS" ? true : false} {...(invitationType === 'SMS' && { suffixIcon: PhoneVerificationICon })} setValue={setValue} setError={setError} clearErrors={clearErrors} 
                                                onBlur={(e) => handleAutoSave('mobilePhone', e?.target?.value)}/>
                                                <FieldErrorMessageRender error={errors} fieldKey='mobilePhone'/>
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={6}>
                                                    <FxTextEdit register={{ ...register("email") }} type="email" className={errors.email ? "border-error-input fx-input-edit fs-mask" : "fx-input-edit login-email fs-mask"} control={control} rules={setEmailValidationOptions} id="signup-individual-email" label="Email " name="email" defaultValue={getKey('email')} isEditable={invitationType !== "EMAIL" ? true : false} {...(invitationType === 'EMAIL' && { suffixIcon: PhoneVerificationICon })}  onBlur={(e) => handleAutoSave('email', e?.target?.value)}/>
                                                    <FieldErrorMessageRender error={errors} fieldKey='email'/>
                                                </Grid>
                                                <Grid className={isSmallScreen ? 'fx-margin-top-2' : ''} item xs={12} sm={12}>
                                                    <Divider />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </FxCardBody>
                                    <FxCardFooter id="customer-individual-signup-Card-Footer" className={isSmallScreen? "fx-sticky-button-form-container fx-footer":"fx-footer"}>
                                        {!isSmallScreen && <Grid container direction="row" justifyContent="flex-start" className="fx-modal-footer fx-footer-kyc">
                                            <Typography id="business-details-card-title" className="fx-footer-kyc-text">   
                                            Your changes are autosaved
                                            </Typography>
                                        </Grid>}
                                        <Grid container direction="row" justifyContent="flex-end" className="fx-modal-footer">
                                            <span className="fx-padding-right-16" />
                                            <FxButton
                                                disableRipple={false}
                                                className="fx-button fx-button-theme"
                                                id="add-mailing-address-save-changes"
                                                type="submit"
                                                isSubmitting={props?.isLoading}
                                            > CONTINUE
                                            </FxButton>
                                        </Grid>
                                    </FxCardFooter>
                                </form>}
                            </div>
                        </FxCard>
                    </Grid>
                </Grid>
            </Grid>
        );
    }));